import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import EditIcon from '@mui/icons-material/Edit';
import PriceAnalysisIcon from '@mui/icons-material/PriceCheck';
import RecommendIcon from '@mui/icons-material/Recommend';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import { GridColDef, GridFilterModel, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';

import TextFieldDebounce from 'components/UI/TextFieldDebounce';
import BusinessUnitSelector from 'components/common/BusinessUnitsSelector';
import CustomDataGrid from 'components/common/CustomDataGrid';
import useCurrentUser from 'hooks/useCurrentUser';
import { useGetCarsQuery } from 'services/api/cars.api';
import priceFormat from 'services/format/priceFormat';
import { Car } from 'services/types/car';

import PriceAnalysisModal from './PriceAnalysisModal';
import RecommendClientsModal from './RecommendClientsModal';
import SoldCarsAnalysis from './SoldCarsAnalysis';

const Cars = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [businessUnitId, setBusinessUnitId] = useState<number>(-1);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 50 });
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [openSoldCarsAnalysis, setOpenSoldCarsAnalysis] = useState(false);
  const navigate = useNavigate();
  const user = useCurrentUser();

  const { data: carsData } = useGetCarsQuery({
    businessUnitId,
    paginationModel: JSON.stringify(paginationModel),
    filterModel: JSON.stringify(filterModel),
    sortModel: JSON.stringify(sortModel),
    searchTerm,
  });

  const selectedCarId = searchParams.get('id') ? parseInt(searchParams.get('id') || '', 10) : undefined;
  const isAnalyzePriceOpen = searchParams.get('analyzePrice') === 'true';
  const isRecommendClientsOpen = searchParams.get('recommendClients') === 'true';

  const handleOpenRecommendModal = (car: Car) => {
    setSearchParams((prev) => {
      prev.set('id', car.id.toString());
      prev.set('recommendClients', 'true');
      return prev;
    });
  };

  const handleOpenPriceAnalysisModal = (car: Car) => {
    setSearchParams((prev) => {
      prev.set('id', car.id.toString());
      prev.set('analyzePrice', 'true');
      return prev;
    });
  };

  const handleOpenEditModal = (car: Car) => {
    navigate(`/cars/${car.id}/edit`);
  };

  const handleCloseRecommendModal = () => {
    setSearchParams((prev) => {
      prev.delete('recommendClients');
      return prev;
    });
  };

  const handleClosePriceAnalysisModal = () => {
    setSearchParams((prev) => {
      prev.delete('analyzePrice');
      return prev;
    });
  };

  const columns: GridColDef[] = [
    { field: 'id', width: 70, headerName: 'ID Zeller' },
    {
      field: 'businessUnit',
      headerName: 'Unidad de Negocio',
      width: 200,
      valueFormatter: (value: { name: string }) => value.name,
    },
    { field: 'referenceId', headerName: 'ID Empresa' },
    { field: 'brand', width: 130, headerName: 'Marca' },
    { field: 'model', width: 130, headerName: 'Modelo' },
    { field: 'version', width: 250, headerName: 'Versión' },
    { field: 'year', width: 70, headerName: 'Año' },
    { field: 'km', width: 80, headerName: 'Kms' },
    {
      field: 'price',
      width: 120,
      valueFormatter: (value) => priceFormat.format(value as number),
      headerName: 'Precio',
    },
    { field: 'promotions', width: 300, headerName: 'Promociones' },
    { field: 'transmission', width: 120, headerName: 'Transmisión' },
    { field: 'carType', width: 120, headerName: 'Tipo' },
    { field: 'fuelType', width: 130, headerName: 'Combustible' },
    { field: 'isUsed', width: 70, valueFormatter: (value) => (value ? 'Usado' : 'Nuevo'), headerName: 'Tipo Venta' },
    { field: 'branchOfficeDirection', width: 250, headerName: 'Sucursal' },
    { field: 'color', width: 150, headerName: 'Color' },
    { field: 'createdAt', width: 200, headerName: 'Creación' },
    { field: 'updatedAt', width: 200, headerName: 'Actualización' },
    { field: 'licensePlate', width: 100, headerName: 'Patente' },
    {
      field: 'link',
      width: 100,
      renderCell: (params) =>
        params.value ? (
          <a href={params.value as string} target="_blank" rel="noreferrer">
            Ver auto
          </a>
        ) : (
          '-'
        ),
    },
    {
      field: 'image',
      width: 100,
      renderCell: (params) =>
        params.value ? (
          <a href={params.value as string} target="_blank" rel="noreferrer">
            Ver foto
          </a>
        ) : (
          '-'
        ),
    },
    {
      field: 'technicalSheet',
      width: 100,
      renderCell: (params) =>
        params.value ? (
          <a href={params.value as string} target="_blank" rel="noreferrer">
            Ficha técnica
          </a>
        ) : (
          '-'
        ),
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: user?.isAdmin ? 320 : 120,
      renderCell: (params) => (
        <div>
          <IconButton
            size="small"
            color="primary"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenEditModal(params.row as Car);
            }}
          >
            <EditIcon />
          </IconButton>
          <Button
            startIcon={<RecommendIcon />}
            size="small"
            variant="outlined"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenRecommendModal(params.row as Car);
            }}
            sx={user?.isAdmin ? { ml: 1 } : { display: 'none' }}
          >
            Clientes
          </Button>
          <Button
            startIcon={<PriceAnalysisIcon />}
            size="small"
            variant="outlined"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenPriceAnalysisModal(params.row as Car);
            }}
            sx={user?.isAdmin ? { ml: 1 } : { display: 'none' }}
          >
            Analizar precio
          </Button>
        </div>
      ),
    },
  ];

  if (user?.isAdmin) {
    columns.push({
      field: 'secondaryPrice',
      width: 120,
      valueFormatter: (value) => priceFormat.format(value as number),
      headerName: 'Precio Secundario',
    });
    columns.push({
      field: 'monthlyFee',
      width: 100,
      valueFormatter: (value) => priceFormat.format(value as number),
      headerName: 'Cuota Mensual',
    });
    columns.push({ field: 'category', width: 120, headerName: 'Categoría' });
    columns.push({ field: 'equipment', width: 500, renderCell: (params) => JSON.stringify(params.value) });
  }

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          gap: 2,
          pt: 1.5,
          px: 2,
          flexShrink: 0,
        }}
      >
        <Box display="flex" alignItems="center" pl={{ xs: 5, md: 0 }}>
          <Typography variant="h5" textAlign="left">
            Lista de Autos
          </Typography>
          {user?.isAdmin && (
            <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
              <Button onClick={() => navigate('/carModels')}>Ver Modelos</Button>
              <Button onClick={() => setOpenSoldCarsAnalysis(true)}>Análisis de Ventas</Button>
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextFieldDebounce
            placeholder="Buscar"
            value={searchTerm}
            onChange={(value) => setSearchTerm(value)}
            size="medium"
          />
          <Select
            value={
              (filterModel.items.find((item) => item.field === 'isUsed')?.value as 'true' | 'false' | undefined) ??
              ('null' as const)
            }
            onChange={(e) => {
              const newFilterModel = { items: [...filterModel.items] };
              const isUsedIndex = newFilterModel.items.findIndex((item) => item.field === 'isUsed');

              if (e.target.value === 'null') {
                if (isUsedIndex !== -1) {
                  newFilterModel.items.splice(isUsedIndex, 1);
                }
              } else {
                const isUsedValue = e.target.value === 'true';
                if (isUsedIndex !== -1) {
                  newFilterModel.items[isUsedIndex].value = isUsedValue;
                } else {
                  newFilterModel.items.push({
                    field: 'isUsed',
                    operator: 'equals',
                    value: isUsedValue,
                  });
                }
              }

              setFilterModel(newFilterModel);
            }}
          >
            <MenuItem value="null">Usados y Nuevos</MenuItem>
            <MenuItem value="true">Usados</MenuItem>
            <MenuItem value="false">Nuevos</MenuItem>
          </Select>
          <BusinessUnitSelector
            businessUnitId={businessUnitId}
            setBusinessUnitId={(value: number) => {
              setBusinessUnitId(value);
            }}
          />
        </Box>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', p: 1 }}>
        {carsData && !openSoldCarsAnalysis && (
          <CustomDataGrid<Car>
            rows={carsData.cars}
            columns={columns}
            filterModel={filterModel}
            sortModel={sortModel}
            onFilterModelChange={setFilterModel}
            onSortModelChange={setSortModel}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            rowCount={carsData.total}
            getRowClassName={(params) => {
              return params.id === selectedCarId ? 'highlighted-1' : '';
            }}
            pageId="cars"
          />
        )}
      </Box>

      {selectedCarId && isRecommendClientsOpen && (
        <RecommendClientsModal open onClose={handleCloseRecommendModal} carId={selectedCarId} />
      )}
      {selectedCarId && isAnalyzePriceOpen && (
        <PriceAnalysisModal open onClose={handleClosePriceAnalysisModal} carId={selectedCarId} />
      )}
      {openSoldCarsAnalysis && (
        <SoldCarsAnalysis businessUnitId={businessUnitId} onClose={() => setOpenSoldCarsAnalysis(false)} />
      )}
    </div>
  );
};

export default Cars;
